import { object, string, array } from "yup";
import { stringExists, USPhoneString } from "./helpers";
import { addressFieldsForBusiness } from "./address";
import {
  isFormattedDate,
  isInBusinessAgeRange,
  isAbn,
  isAcn,
  isDateInThePast
} from "../";
import { overSome } from "lodash";
import { isEurope, SUPPORTED_APAC_COUNTRY_CODES } from "../../countryHelper";

const REGISTRATION_NUMBER_ALLOWED_CHARACTERS = /^[A-Za-z0-9.-]+$/;
const FISERV_EU_LOCATION_PHONE_REGEX = /^\d{5,15}$/;
const FISERV_EU_LOCATION_PHONE_MESSAGE =
  "Phone number must be between 5 and 15 digits";
const COUNTRY_CODE = "$countryCode";
const PLEASE_SELECT_AN_OPTION = "Please select an option from the list";
const PLEASE_UPLOAD_YOUR_BUSINESS_REGISTRATION =
  "Please upload your business registration document.";

const sanitizeRegistrationNumber = value => value.replace(/[.-]/g, "");

const hasValidSanitizedRegistrationNumberLength = value =>
  sanitizeRegistrationNumber(value).length <= 15;

export default object().shape({
  dba_name: stringExists,
  description: stringExists,
  // $FlowFixMe
  tax_id: string().when(
    [COUNTRY_CODE, "registered_as"],
    (countryCode, registeredAs, schema) => {
      if (countryCode === "USA") {
        if (registeredAs === "sole_proprietorship") {
          return schema.notRequired();
        }
        return schema
          .matches(/^\d+$/, {
            message: "Sorry, only numbers (0-9) are allowed",
            excludeEmptyString: true
          })
          .length(
            9,
            "Please enter a valid United States Federal Tax Identification Number (TIN)"
          );
      }
      if (countryCode === "AUS") {
        return schema.test(
          "abnOrAcn",
          "Please enter an 11-digit ABN or 9-digit ACN",
          overSome([isAbn, isAcn])
        );
      }
      if (countryCode === "BRA") {
        return schema
          .matches(/^\d+$/, {
            message: "Sorry, only numbers (0-9) are allowed",
            excludeEmptyString: true
          })
          .length(
            14,
            "Please enter a 14 digit valid Brazil Federal Tax Identification Number (CNPJ)"
          );
      }
      if (isEurope(countryCode)) {
        return schema.notRequired();
      }
      return stringExists;
    }
  ),
  registration_number: string().when([COUNTRY_CODE], (countryCode, schema) => {
    if (isEurope(countryCode)) {
      return stringExists
        .matches(REGISTRATION_NUMBER_ALLOWED_CHARACTERS, {
          message:
            "Sorry, only letters, numbers, dots (.) and hyphens (-) are allowed",
          excludeEmptyString: true
        })
        .test(
          "sanitizedMaxLength",
          "Registration Number must be 15 characters or less",
          value => !value || hasValidSanitizedRegistrationNumberLength(value)
        );
    }
    return schema.notRequired();
  }),
  established_on: stringExists
    .test(
      "format",
      "Please use a YYYY-MM-DD or MM/DD/YYYY format",
      isFormattedDate
    )
    .test("age", "A valid date is required", isInBusinessAgeRange)
    .test("date", "Please select a date in the past", isDateInThePast),
  legal_name: string().when([COUNTRY_CODE, "registered_as"], {
    is: (countryCode, registeredAs) =>
      countryCode === "USA" && registeredAs === "sole_proprietorship",
    then: string().notRequired(),
    otherwise: stringExists
  }),
  registered_as: string().required(PLEASE_SELECT_AN_OPTION),
  industry: string().when(["$isReopening"], (isReopening, schema) => {
    if (isReopening) {
      return schema.notRequired();
    }
    return schema.required(PLEASE_SELECT_AN_OPTION);
  }),
  mcc: string().notRequired(),
  // $FlowFixMe
  state_incorporated: string().when(
    [COUNTRY_CODE, "registered_as"],
    (countryCode, registeredAs, schema) => {
      if (countryCode === "USA") {
        if (registeredAs === "sole_proprietorship") {
          return schema.notRequired();
        }
        return schema.required(PLEASE_SELECT_AN_OPTION);
      }
      return schema.notRequired();
    }
  ),
  phone: string().when(
    [COUNTRY_CODE, "$features"],
    (countryCode, features, schema) => {
      if (isEurope(countryCode)) {
        return stringExists.matches(FISERV_EU_LOCATION_PHONE_REGEX, {
          message: FISERV_EU_LOCATION_PHONE_MESSAGE,
          excludeEmptyString: true
        });
      }

      if (countryCode === "USA") {
        return USPhoneString;
      }

      if (countryCode === "AUS" && features?.validateAuPhoneLength) {
        return schema
          .ensure()
          .required("Please enter a phone number with country code")
          .matches(/^\d{10}$/, {
            message: "Phone number must be exactly 10 digits",
            excludeEmptyString: true
          });
      }

      return schema
        .ensure()
        .required("Please enter a phone number with country code");
    }
  ),
  website: stringExists.url(
    `A valid URL including "http://" or "https://" is required`
  ),
  address: addressFieldsForBusiness,
  business_location_type: string().when(
    [COUNTRY_CODE],
    (countryCode, schema) => {
      if (countryCode === "CAN") {
        return schema.required(PLEASE_SELECT_AN_OPTION);
      }
      return schema.notRequired();
    }
  ),
  staging_website_url: string().notRequired(),
  staging_login: string().notRequired(),
  staging_password: string().notRequired(),
  email: string().when(["$venmoOnly"], {
    is: venmoOnly => venmoOnly,
    then: stringExists,
    otherwise: string().notRequired()
  }),
  logo_image_id: string().when(["$venmoOnly", "pay_with_venmo"], {
    is: (venmoOnly, pay_with_venmo) => venmoOnly || pay_with_venmo === "1",
    then: stringExists,
    otherwise: string().notRequired()
  }),
  documents: array().when([COUNTRY_CODE, "$isReopening"], {
    is: (countryCode, isReopening) =>
      !isReopening && SUPPORTED_APAC_COUNTRY_CODES.includes(countryCode),
    then: array()
      .min(1, PLEASE_UPLOAD_YOUR_BUSINESS_REGISTRATION)
      .required(PLEASE_UPLOAD_YOUR_BUSINESS_REGISTRATION),
    otherwise: array().notRequired()
  }),
  pay_with_venmo: string().notRequired()
});
